import React from 'react';
import { motion } from 'framer-motion';
import { AccountCard } from '../../components/AccountCard';
import { GroupCard } from '../../components/GroupCard';
import { GroupSummaryCard } from '../../components/GroupSummaryCard';
import { BulkActionBar } from '../../components/BulkActionBar';
import {
  BulkActionSheet,
  BulkResultModal,
  type SkippedGroup,
} from '../../components/BulkActionModals';
import { IAccountDataResponse, IGroupData } from '@tool-no-hu/shared/types';
import type { BulkGroupAction, IBulkGroupActionResult } from '@tool-no-hu/shared/types';
import { deleteAccountService, getAccountsService } from '../../service/account';
import {
  getSunwinBalanceService,
  sunwinWithdrawService,
  getSunwinCryptoAddressService,
  getSunwinCryptoOptionsService,
  getSunwinTransactionHistoryService,
  getSunwinBetLogsService,
  sunwinSyncBetLogsService,
  sunwinSyncTransactionsService,
  sunwinLoginAccountService,
  sunwinLogoutAccountService,
  sunwinBulkGroupActionService,
  type CryptoOptionItem,
} from '../../service/sunwin';
import { getGroupsService } from '../../service/group';
import { GroupConfigPopup } from '../../components/GroupConfigPopup';
import { showToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useSyncEvents } from '../../hooks/useSyncEvents';
import { useGroupSelection } from '../../hooks/useGroupSelection';
import { groupEligibility } from '../../utils/group-eligibility';
import {
  Users,
  Activity,
  TrendingUp,
  TrendingDown,
  BarChart2,
  X,
  Gamepad2,
  LayoutGrid,
  List,
  ListChecks,
} from 'lucide-react';

// ─── CountUp ──────────────────────────────────────────────────────────────

function useCountUp(end: number, duration = 800): number {
  const [val, setVal] = React.useState(0);
  const prevRef = React.useRef(0);

  React.useEffect(() => {
    let raf: number;
    let startTime: number | null = null;
    const startVal = prevRef.current;

    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const elapsed = ts - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startVal + (end - startVal) * eased);
      setVal(current);
      if (progress < 1) raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);
    prevRef.current = end;
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);

  return val;
}

// ─── Crypto pair picker ───────────────────────────────────────────────────
// Danh sách coin/network KHÔNG hard-code ở FE: paygate bật/tắt coin và đổi chain bất cứ lúc nào
// (đã quan sát USDT đổi chain rút TRC20 → BEP20). Mọi lựa chọn đọc từ /crypto-options.
// Network nạp và network rút là 2 tập khác nhau nên phải truyền direction rõ ràng.

type CryptoDirection = 'deposit' | 'withdraw';

function networksFor(item: CryptoOptionItem, direction: CryptoDirection): string[] {
  return direction === 'deposit' ? item.depositNetworks : item.withdrawalNetworks;
}

/** Coin dùng được cho chiều đang xét — coin có network rỗng nghĩa là paygate đã tắt chiều đó */
function usableOptions(items: CryptoOptionItem[], direction: CryptoDirection): CryptoOptionItem[] {
  return items.filter((item) => networksFor(item, direction).length > 0);
}

function networksOfCurrency(
  items: CryptoOptionItem[],
  currency: string,
  direction: CryptoDirection,
): string[] {
  const item = items.find((entry) => entry.currency === currency);
  return item ? networksFor(item, direction) : [];
}

/** Chỉ có 1 lựa chọn thì chọn sẵn — không bắt user chọn cái duy nhất; mơ hồ thì để trống */
function autoPickPair(
  items: CryptoOptionItem[],
  direction: CryptoDirection,
): { currency: string; network: string } {
  const usable = usableOptions(items, direction);
  const only = usable.length === 1 ? usable[0] : undefined;
  if (!only) return { currency: '', network: '' };
  const networks = networksFor(only, direction);
  return {
    currency: only.currency,
    network: networks.length === 1 ? (networks[0] ?? '') : '',
  };
}

/** Coin/network mặc định khi mở form nạp — vẫn chỉnh lại được, chỉ đỡ phải chọn tay mỗi lần. */
const DEFAULT_DEPOSIT_CURRENCY = 'USDT';
const DEFAULT_DEPOSIT_NETWORK = 'BEP20';

/**
 * Nạp thì luôn ưu tiên USDT/BEP20 nếu paygate đang cho phép — đây là cặp khách
 * dùng nhiều nhất. Không có thì rơi về autoPickPair (chọn sẵn khi chỉ có 1 lựa chọn).
 */
function autoPickDepositPair(items: CryptoOptionItem[]): { currency: string; network: string } {
  const usdt = usableOptions(items, 'deposit').find(
    (item) => item.currency === DEFAULT_DEPOSIT_CURRENCY,
  );
  if (usdt && networksFor(usdt, 'deposit').includes(DEFAULT_DEPOSIT_NETWORK)) {
    return { currency: DEFAULT_DEPOSIT_CURRENCY, network: DEFAULT_DEPOSIT_NETWORK };
  }
  return autoPickPair(items, 'deposit');
}

/** Nhịp hỏi lại số dư khi modal nạp đang mở, và trần thời gian tự dừng. */
const DEPOSIT_POLL_INTERVAL_MS = 15_000;
const DEPOSIT_POLL_MAX_MS = 5 * 60_000;

/** Trần nhịp refetch accounts+groups khi SSE sync event về dồn dập. */
const SYNC_REFRESH_THROTTLE_MS = 1_000;

/**
 * Số dòng lịch sử cược hiển thị — khớp đúng `BETLOG_KEEP_PER_ACCOUNT` (60) bên
 * server. Xin 30 như trước là tự bỏ mất nửa số bản ghi đang có: prune đã cắt
 * lịch sử xuống rất ngắn rồi (mỗi phiên rải 25-46 gói = 25-46 dòng, nên 60 ≈
 * 1-2 phiên), không việc gì phải cắt thêm ở tầng hiển thị.
 */
const BETLOG_PAGE_SIZE = 60;

const selectClass =
  'w-full rounded-lg border border-[#252a35] bg-[#0d0f14] px-3 py-2.5 text-sm text-[#e2e8f0] outline-none transition-colors focus:border-[#3b82f6] disabled:cursor-not-allowed disabled:opacity-60';

const labelClass = 'mb-1 block text-[11px] font-bold uppercase tracking-wider text-[#64748b]';

const CryptoPairPicker: React.FC<{
  direction: CryptoDirection;
  options: CryptoOptionItem[];
  currency: string;
  network: string;
  loading: boolean;
  disabled?: boolean;
  onChange: (next: { currency: string; network: string }) => void;
}> = ({ direction, options, currency, network, loading, disabled, onChange }) => {
  const usable = usableOptions(options, direction);
  const networks = networksOfCurrency(options, currency, direction);
  const verb = direction === 'deposit' ? 'nạp' : 'rút';

  if (loading) {
    return (
      <p className="text-xs font-medium text-[#64748b]">Đang tải cấu hình {verb} từ hệ thống...</p>
    );
  }

  if (usable.length === 0) {
    return (
      <div className="rounded-xl border border-[#ef4444]/20 bg-[#ef4444]/10 p-3">
        <span className="text-[11px] font-semibold leading-normal text-[#ef4444]">
          Hệ thống hiện không cho {verb} loại tiền điện tử nào. Vui lòng thử lại sau.
        </span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className={labelClass}>Loại tiền</label>
        <select
          value={currency}
          disabled={disabled}
          onChange={(e) => {
            const nextCurrency = e.target.value;
            const nextNetworks = networksOfCurrency(options, nextCurrency, direction);
            // Coin chỉ có 1 network thì chọn luôn, khỏi bắt user click thêm một lần nữa
            onChange({
              currency: nextCurrency,
              network: nextNetworks.length === 1 ? (nextNetworks[0] ?? '') : '',
            });
          }}
          className={selectClass}
        >
          <option value="">-- Chọn --</option>
          {usable.map((item) => (
            <option key={item.currency} value={item.currency}>
              {item.currency}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelClass}>Mạng lưới</label>
        <select
          value={network}
          disabled={disabled || !currency || networks.length === 0}
          onChange={(e) => onChange({ currency, network: e.target.value })}
          className={selectClass}
        >
          <option value="">-- Chọn --</option>
          {networks.map((net) => (
            <option key={net} value={net}>
              {net}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// ─── StatCard ─────────────────────────────────────────────────────────────

const statConfig = [
  { label: 'Tài khoản', icon: Users, color: 'text-[#e2e8f0]' },
  { label: 'Đang chạy', icon: Activity, color: 'text-[#3b82f6]' },
  { label: 'Thắng', icon: TrendingUp, color: 'text-[#22c55e]' },
  { label: 'Thua', icon: TrendingDown, color: 'text-[#ef4444]' },
  { label: 'Tỷ lệ thắng', icon: BarChart2, color: 'text-[#f59e0b]' },
];

type FilterPeriod = 'today' | '7d' | 'all';

interface StatCardProps {
  label: string;
  icon: React.FC<{ className?: string }>;
  value: number | string;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, icon: Icon, value, color }) => {
  const numericVal = typeof value === 'number' ? value : 0;
  const countUp = useCountUp(numericVal);

  return (
    <div className="flex flex-col items-center justify-center rounded-lg bg-[#161920] py-3">
      <div className="flex items-center gap-1.5">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className={`font-mono text-xl font-extrabold ${color}`}>
          {typeof value === 'string' ? value : countUp.toLocaleString('vi-VN')}
        </span>
      </div>
      <span className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
        {label}
      </span>
    </div>
  );
};

// ─── Account Page ─────────────────────────────────────────────────────────

export const Lobby: React.FC = () => {
  const [accounts, setAccounts] = React.useState<IAccountDataResponse[]>([]);
  const [groups, setGroups] = React.useState<IGroupData[]>([]);
  const [loading, setLoading] = React.useState(true);
  const confirm = useConfirm();

  // Filter period + auto-refresh key (for progress bar reset)
  const [filterPeriod] = React.useState<FilterPeriod>('all');
  const [refreshKey, setRefreshKey] = React.useState(0);

  // ── Data fetching ──────────────────────────────────────────────────

  const fetchAccounts = async () => {
    try {
      const data = await getAccountsService('LIST_DATA');
      const fresh = data as IAccountDataResponse[];
      setAccounts((prev) =>
        fresh.map((acc) =>
          mergeAccountTokens(
            acc,
            prev.find((p) => p._id === acc._id),
          ),
        ),
      );
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const data = await getGroupsService();
      setGroups(data);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  };

  React.useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchAccounts(), fetchGroups()]);
      setLoading(false);
    };
    loadAll();
  }, []);

  // Auto-refresh 15s — reset progress bar every cycle
  // Preserve all fields nếu fresh data chưa kịp update (race với login/sync/auto-refresh)
  const mergeAccountTokens = (
    fresh: IAccountDataResponse,
    existing?: IAccountDataResponse,
  ): IAccountDataResponse => {
    if (!existing) return fresh;
    return {
      ...fresh,
      status: fresh.status ?? existing.status ?? 'STOPPED',
      // Token fields — giữ value cũ nếu fresh null
      accessToken: fresh.accessToken || existing.accessToken,
      wsToken: fresh.wsToken || existing.wsToken,
      signature: fresh.signature || existing.signature,
      loginInfo: fresh.loginInfo || existing.loginInfo,
      customerId: fresh.customerId || existing.customerId,
      tokenTime: fresh.tokenTime || existing.tokenTime,
      // Balance/profit — nếu fresh không có token (stale từ DB chưa sync),
      // giữ value cũ từ existing (vừa được login/sync cập nhật)
      balance:
        fresh.accessToken || !existing.accessToken
          ? fresh.balance
          : fresh.balance || existing.balance,
    };
  };

  const fetchAccountsSilent = React.useCallback(async () => {
    try {
      const data = await getAccountsService('LIST_DATA', { silent: true });
      const fresh = data as IAccountDataResponse[];
      setAccounts((prev) =>
        fresh.map((acc) =>
          mergeAccountTokens(
            acc,
            prev.find((p) => p._id === acc._id),
          ),
        ),
      );
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  }, []);

  const fetchGroupsSilent = React.useCallback(async () => {
    try {
      const data = await getGroupsService({ silent: true });
      setGroups(data);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  }, []);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setRefreshKey((k) => k + 1);
      void fetchAccountsSilent();
      void fetchGroupsSilent();
    }, 30000);
    return () => clearInterval(timer);
  }, [fetchAccountsSilent, fetchGroupsSilent]);

  // SSE sync events
  const fetchAccountsSilentRef = React.useRef(fetchAccountsSilent);
  const fetchGroupsSilentRef = React.useRef(fetchGroupsSilent);
  fetchAccountsSilentRef.current = fetchAccountsSilent;
  fetchGroupsSilentRef.current = fetchGroupsSilent;

  // Worker sync mỗi account một cách độc lập nên SSE bắn event dồn dập —
  // 6 nhóm đang chạy có thể đẩy hàng chục event trong vài giây, mỗi cái kéo
  // theo 2 lượt tải lại toàn bộ accounts + groups. Throttle leading-edge:
  // event đầu của mỗi đợt refetch ngay (UI không trễ), phần còn lại gom vào
  // đúng một lượt ở cuối cửa sổ.
  const lastSyncRefreshRef = React.useRef(0);
  const syncRefreshTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (syncRefreshTimerRef.current) clearTimeout(syncRefreshTimerRef.current);
    },
    [],
  );

  useSyncEvents(() => {
    const runRefresh = () => {
      lastSyncRefreshRef.current = Date.now();
      setRefreshKey((k) => k + 1);
      void fetchAccountsSilentRef.current();
      void fetchGroupsSilentRef.current();
    };

    const elapsed = Date.now() - lastSyncRefreshRef.current;
    if (elapsed >= SYNC_REFRESH_THROTTLE_MS) {
      runRefresh();
      return;
    }

    // Đã có lượt đang chờ thì để nó chạy, không dời hạn (tránh starvation khi
    // event về liên tục không ngơi).
    if (syncRefreshTimerRef.current) return;

    syncRefreshTimerRef.current = setTimeout(() => {
      syncRefreshTimerRef.current = null;
      runRefresh();
    }, SYNC_REFRESH_THROTTLE_MS - elapsed);
  });

  // ── Computed stats ─────────────────────────────────────────────────

  const totalAccounts = accounts.length;
  const activeAccounts = accounts.filter((acc) => acc.status === 'RUNNING').length;
  const totalWins = accounts.reduce((sum, acc) => sum + (acc.rounds?.w || 0), 0);
  const totalLosses = accounts.reduce((sum, acc) => sum + (acc.rounds?.l || 0), 0);
  const winRate =
    totalWins + totalLosses > 0
      ? `${Math.round((totalWins / (totalWins + totalLosses)) * 100)}%`
      : 'N/A';

  const [groupEditTarget, setGroupEditTarget] = React.useState<IGroupData | null>(null);

  // ── View mode (Chi tiết ⇄ Tóm tắt) + chọn nhiều nhóm + bulk ────────────
  const [groupView, setGroupView] = React.useState<'detail' | 'summary'>(() => {
    try {
      return localStorage.getItem('lobby-view') === 'summary' ? 'summary' : 'detail';
    } catch {
      return 'detail';
    }
  });
  const setGroupViewMode = React.useCallback((next: 'detail' | 'summary') => {
    setGroupView(next);
    try {
      localStorage.setItem('lobby-view', next);
    } catch {
      // localStorage bị chặn (ẩn danh) — không nhớ được thì thôi, không chặn UI.
    }
  }, []);

  const selection = useGroupSelection();
  const [bulkPending, setBulkPending] = React.useState<{
    action: BulkGroupAction;
    run: IGroupData[];
    skip: SkippedGroup[];
  } | null>(null);
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const [bulkResults, setBulkResults] = React.useState<{
    action: BulkGroupAction;
    results: IBulkGroupActionResult[];
  } | null>(null);

  const selectedGroups = React.useMemo(
    () => groups.filter((g) => selection.selected.has(String(g._id))),
    [groups, selection.selected],
  );

  /** Số nhóm đủ điều kiện từng hành động — hiện trên badge của thanh bulk. */
  const eligibleCounts = React.useMemo(() => {
    let start = 0;
    let stop = 0;
    for (const group of selectedGroups) {
      const eligibility = groupEligibility(group, accounts);
      if (eligibility.start.ok) start += 1;
      if (eligibility.stop.ok) stop += 1;
    }
    return { start, stop };
  }, [selectedGroups, accounts]);

  /** Bấm một hành động ở thanh bulk → chia đủ/không đủ điều kiện rồi mở bảng. */
  const openBulk = (action: BulkGroupAction) => {
    const run: IGroupData[] = [];
    const skip: SkippedGroup[] = [];
    for (const group of selectedGroups) {
      const verdict = groupEligibility(group, accounts)[action];
      if (verdict.ok) run.push(group);
      else skip.push({ group, reason: verdict.reason });
    }
    setBulkPending({ action, run, skip });
  };

  const confirmBulk = async () => {
    if (!bulkPending) return;
    setBulkBusy(true);
    try {
      const results = await sunwinBulkGroupActionService(
        bulkPending.action,
        bulkPending.run.map((group) => String(group._id)),
      );
      setBulkResults({ action: bulkPending.action, results });
      setBulkPending(null);
      selection.toggleSelecting(); // thoát chế độ chọn + xoá lựa chọn
      await Promise.all([fetchAccounts(), fetchGroups()]);
    } catch {
      showToast.error('Thao tác hàng loạt thất bại');
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleSelectAll = () => {
    const allIds = groups.map((group) => String(group._id));
    // So theo số nhóm THỰC được chọn (đã lọc id rác khỏi nhóm đã xoá), không phải
    // `selected.size` — Set có thể còn id của nhóm vừa bị auto-refresh xoá, khiến
    // "chọn tất cả" tính sai.
    selection.replace(selectedGroups.length === allIds.length ? [] : allIds);
  };

  // ── Modal state ────────────────────────────────────────────────────

  // Modal nạp 2 bước: chọn coin/network (từ config live) → lấy địa chỉ + QR
  const [depositModal, setDepositModal] = React.useState<{
    isOpen: boolean;
    id: string;
    username: string;
    options: CryptoOptionItem[];
    currency: string;
    network: string;
    loadingOptions: boolean;
    fetchingAddress: boolean;
    address: string;
    qrCode: string;
  }>({
    isOpen: false,
    id: '',
    username: '',
    options: [],
    currency: '',
    network: '',
    loadingOptions: false,
    fetchingAddress: false,
    address: '',
    qrCode: '',
  });

  // Tiền crypto về ví là bất đồng bộ (paygate xử lý xong mới cộng) nên không có
  // mốc nào để refetch. Khi modal nạp đang mở thì chủ động hỏi lại số dư để khách
  // thấy tiền vào ngay, thay vì phải chờ cycle sync của worker.
  React.useEffect(() => {
    if (!depositModal.isOpen || !depositModal.id) return;

    const accountId = depositModal.id;
    const startedAt = Date.now();

    const timer = setInterval(() => {
      if (Date.now() - startedAt >= DEPOSIT_POLL_MAX_MS) {
        clearInterval(timer);
        return;
      }
      void (async () => {
        // silent: không bắn toast mỗi 15s nếu account rớt token
        const updatedAcc = await getSunwinBalanceService(accountId, true);
        if (!updatedAcc) return;
        setAccounts((prev) =>
          prev.map((acc) => (acc._id === accountId ? { ...acc, ...updatedAcc } : acc)),
        );
      })();
    }, DEPOSIT_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [depositModal.isOpen, depositModal.id]);

  const [historyModal, setHistoryModal] = React.useState<{
    isOpen: boolean;
    id: string;
    username: string;
    slipType: number;
    items: any[];
    loading: boolean;
    syncing: boolean;
  }>({
    isOpen: false,
    id: '',
    username: '',
    slipType: 1,
    items: [],
    loading: false,
    syncing: false,
  });

  const [betLogModal, setBetLogModal] = React.useState<{
    isOpen: boolean;
    id: string;
    username: string;
    items: any[];
    loading: boolean;
    syncing: boolean;
  }>({
    isOpen: false,
    id: '',
    username: '',
    items: [],
    loading: false,
    syncing: false,
  });

  const [withdrawModal, setWithdrawModal] = React.useState<{
    isOpen: boolean;
    id: string;
    username: string;
    address: string;
    amount: string;
    options: CryptoOptionItem[];
    currency: string;
    network: string;
    loadingOptions: boolean;
    submitting: boolean;
  }>({
    isOpen: false,
    id: '',
    username: '',
    address: '',
    amount: '',
    options: [],
    currency: '',
    network: '',
    loadingOptions: false,
    submitting: false,
  });

  // ── Handlers ───────────────────────────────────────────────────────

  const fetchTransactionHistory = async (id: string, slipType: number) => {
    const targetAcc = accounts.find((acc) => acc._id === id);
    const username = targetAcc ? targetAcc.username : '';
    setHistoryModal((prev) => ({
      ...prev,
      isOpen: true,
      id,
      username,
      slipType,
      loading: true,
      syncing: true,
      items: [],
    }));

    getSunwinTransactionHistoryService(id, slipType, 20, 0)
      .then((data) => {
        setHistoryModal((prev) => ({
          ...prev,
          items: data?.items || [],
          loading: false,
        }));
      })
      .catch((err) => {
        console.error(err);
        setHistoryModal((prev) => ({ ...prev, loading: false }));
      });

    sunwinSyncTransactionsService(id)
      .then(async () => {
        const refreshed = await getSunwinTransactionHistoryService(id, slipType, 20, 0);
        setHistoryModal((prev) => ({
          ...prev,
          items: refreshed?.items || prev.items,
          syncing: false,
        }));
      })
      .catch((err) => {
        console.error(err);
        setHistoryModal((prev) => ({ ...prev, syncing: false }));
      });
  };

  const handleSwitchSlipType = async (slipType: number) => {
    if (!historyModal.id) return;
    setHistoryModal((prev) => ({ ...prev, slipType, loading: true, items: [] }));
    try {
      const data = await getSunwinTransactionHistoryService(historyModal.id, slipType, 20, 0);
      if (data) {
        setHistoryModal((prev) => ({
          ...prev,
          items: data.items || [],
          loading: false,
        }));
      } else {
        setHistoryModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error(err);
      setHistoryModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleGetBetLogs = async (id: string) => {
    const targetAcc = accounts.find((acc) => acc._id === id);
    const username = targetAcc ? targetAcc.username : '';
    setBetLogModal({
      isOpen: true,
      id,
      username,
      items: [],
      loading: true,
      syncing: true,
    });

    // Đọc cache trước để có cái hiện ngay, rồi sync và đọc lại.
    // Trước đây hai lượt đọc này chạy SONG SONG với sync — cùng lúc kéo toàn
    // bộ bet log về hai lần, trong khi sync bắn SSE khiến lobby refetch cả
    // danh sách account lẫn group. Một cú click thành cả chuỗi việc nặng.
    try {
      const cached = await getSunwinBetLogsService(id, BETLOG_PAGE_SIZE);
      setBetLogModal((prev) => ({ ...prev, items: cached?.items ?? [], loading: false }));
    } catch (err) {
      console.error(err);
      setBetLogModal((prev) => ({ ...prev, loading: false }));
    }

    try {
      await sunwinSyncBetLogsService(id);
      const refreshed = await getSunwinBetLogsService(id, BETLOG_PAGE_SIZE);
      setBetLogModal((prev) => ({
        ...prev,
        items: refreshed?.items ?? prev.items,
        syncing: false,
      }));
    } catch (err) {
      console.error(err);
      setBetLogModal((prev) => ({ ...prev, syncing: false }));
    }
  };

  /** Lấy địa chỉ ví + QR cho 1 cặp coin/network — dùng chung cho auto-fetch lẫn nút bấm tay */
  const fetchDepositAddressFor = async (
    id: string,
    currency: string,
    network: string,
  ): Promise<void> => {
    setDepositModal((prev) => (prev.id === id ? { ...prev, fetchingAddress: true } : prev));
    try {
      const data = await getSunwinCryptoAddressService(id, currency, network);
      setDepositModal((prev) =>
        prev.id === id
          ? {
              ...prev,
              fetchingAddress: false,
              address: data?.address ?? '',
              qrCode: data?.qrCode ?? '',
            }
          : prev,
      );
    } catch (err) {
      console.error(err);
      showToast.error('Có lỗi xảy ra khi lấy mã QR nạp.');
      setDepositModal((prev) => (prev.id === id ? { ...prev, fetchingAddress: false } : prev));
    }
  };

  /**
   * Mở modal nạp: tải danh sách coin/network được phép nạp từ paygate rồi tự lấy
   * luôn địa chỉ/QR cho cặp mặc định (USDT/BEP20 hoặc cặp duy nhất khả dụng) —
   * khách không phải bấm thêm bước "Lấy địa chỉ nạp" nếu không cần đổi coin.
   */
  const handleGetUSDTQR = async (id: string) => {
    const targetAcc = accounts.find((acc) => acc._id === id);
    const username = targetAcc ? targetAcc.username : '';
    setDepositModal({
      isOpen: true,
      id,
      username,
      options: [],
      currency: '',
      network: '',
      loadingOptions: true,
      fetchingAddress: false,
      address: '',
      qrCode: '',
    });

    try {
      const config = await getSunwinCryptoOptionsService(id);
      const items = config?.items ?? [];
      const picked = autoPickDepositPair(items);
      setDepositModal((prev) =>
        prev.id === id ? { ...prev, options: items, ...picked, loadingOptions: false } : prev,
      );
      if (picked.currency && picked.network) {
        await fetchDepositAddressFor(id, picked.currency, picked.network);
      }
    } catch (err) {
      console.error(err);
      showToast.error('Có lỗi xảy ra khi lấy cấu hình nạp.');
      setDepositModal((prev) => (prev.id === id ? { ...prev, loadingOptions: false } : prev));
    }
  };

  /** Đổi coin/network tay xong bấm để lấy lại địa chỉ ví + QR cho cặp mới */
  const handleFetchDepositAddress = async () => {
    const { id, currency, network } = depositModal;
    if (!currency || !network) {
      showToast.error('Vui lòng chọn loại tiền và mạng lưới');
      return;
    }
    await fetchDepositAddressFor(id, currency, network);
  };

  const handleDeleteAccount = async (id: string) => {
    const targetAcc = accounts.find((acc) => acc._id === id);
    const username = targetAcc ? targetAcc.username : 'tài khoản';
    const isConfirmed = await confirm({
      title: 'Xoá tài khoản',
      message: `Bạn có chắc chắn muốn xoá tài khoản: ${username}? Toàn bộ lịch sử nạp/rút và betlog của tài khoản này cũng bị xoá vĩnh viễn.`,
      type: 'danger',
    });
    if (isConfirmed) {
      try {
        const success = await deleteAccountService(id);
        await Promise.all([fetchAccounts(), fetchGroups()]);
        if (success) {
          showToast.success(`Đã xoá tài khoản "${username}" thành công!`);
        } else {
          showToast.error(`Xoá tài khoản "${username}" thất bại`);
        }
      } catch (err: any) {
        console.error(err);
        showToast.error(err?.response?.data?.message || 'Có lỗi xảy ra khi xoá tài khoản.');
      }
    }
  };

  const handleGetBalance = async (id: string) => {
    try {
      const updatedAcc = await getSunwinBalanceService(id);
      if (updatedAcc) {
        setAccounts((prev) =>
          prev.map((acc) => (acc._id === id ? { ...acc, ...updatedAcc } : acc)),
        );
      }
    } catch (err) {
      console.error(err);
      showToast.error('Lấy số dư thất bại.');
    }
  };

  const handleOpenWithdraw = (id: string) => {
    const targetAcc = accounts.find((acc) => acc._id === id);
    const username = targetAcc ? targetAcc.username : '';
    setWithdrawModal({
      isOpen: true,
      id,
      username,
      address: '',
      amount: '',
      options: [],
      currency: '',
      network: '',
      loadingOptions: true,
      submitting: false,
    });

    // Tải danh sách coin/network được phép RÚT (khác tập nạp) ngay khi mở form
    void (async () => {
      try {
        const config = await getSunwinCryptoOptionsService(id);
        const items = config?.items ?? [];
        const picked = autoPickPair(items, 'withdraw');
        setWithdrawModal((prev) =>
          prev.id === id ? { ...prev, options: items, ...picked, loadingOptions: false } : prev,
        );
      } catch (err) {
        console.error(err);
        showToast.error('Có lỗi xảy ra khi lấy cấu hình rút.');
        setWithdrawModal((prev) => (prev.id === id ? { ...prev, loadingOptions: false } : prev));
      }
    })();
  };

  const handleWithdrawSubmit = async () => {
    const { id, address, amount, currency, network } = withdrawModal;
    const trimmedAddress = address.trim();
    const numericAmount = Number(amount);

    if (!currency || !network) {
      showToast.error('Vui lòng chọn loại tiền và mạng lưới rút');
      return;
    }
    if (!trimmedAddress) {
      showToast.error(`Vui lòng nhập địa chỉ ví ${currency} (${network})`);
      return;
    }
    if (!amount || isNaN(numericAmount) || numericAmount < 200000) {
      showToast.error('Số tiền rút tối thiểu là 200.000 VND');
      return;
    }

    const isConfirmed = await confirm({
      title: `Xác nhận rút ${currency}`,
      message: `Bạn có chắc chắn muốn rút ${numericAmount.toLocaleString('vi-VN')} VND bằng ${currency} qua mạng lưới ${network} về địa chỉ ${trimmedAddress}?`,
      type: 'danger',
    });
    if (!isConfirmed) return;

    setWithdrawModal((prev) => ({ ...prev, submitting: true }));
    try {
      const result = await sunwinWithdrawService(
        id,
        trimmedAddress,
        numericAmount,
        currency,
        network,
      );
      if (result) {
        showToast.success(
          `Rút ${result.currency} (${result.network}) thành công! Mã giao dịch: ${result.pendingTxId}`,
        );
        setWithdrawModal((prev) => ({ ...prev, isOpen: false, submitting: false }));
        // Optimistic — decrement balance locally
        setAccounts((prev) =>
          prev.map((acc) =>
            acc._id === id && typeof acc.balance === 'number'
              ? {
                  ...acc,
                  balance: acc.balance - numericAmount,
                }
              : acc,
          ),
        );
      } else {
        setWithdrawModal((prev) => ({ ...prev, submitting: false }));
      }
    } catch (err) {
      console.error(err);
      setWithdrawModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  // ── Single account login/logout ──────────────────────────────────

  const handleLoginAccount = async (id: string) => {
    const username = accounts.find((a) => a._id === id)?.username ?? '';
    const res = await sunwinLoginAccountService(id);
    // Endpoint này đánh dấu _skipToast nên caller phải tự báo — trước đây thất
    // bại là im lặng hoàn toàn, bấm Đăng nhập không thấy gì xảy ra.
    if (!res || res.code !== 200) {
      showToast.error(
        `Đăng nhập ${username || 'tài khoản'} thất bại: ${res?.message || 'không kết nối được tới máy chủ'}`,
        7000,
      );
      return;
    }
    if (res.data) {
      setAccounts((prev) => prev.map((acc) => (acc._id === id ? { ...acc, ...res.data } : acc)));
    }
    showToast.success(`Đăng nhập ${username || 'tài khoản'} thành công`);
  };

  const handleLogoutAccount = async (id: string) => {
    const username = accounts.find((a) => a._id === id)?.username ?? '';
    const res = await sunwinLogoutAccountService(id);
    if (!res || res.code !== 200) {
      showToast.error(
        `Đăng xuất ${username || 'tài khoản'} thất bại: ${res?.message || 'không kết nối được tới máy chủ'}`,
        7000,
      );
      return;
    }
    setAccounts((prev) =>
      prev.map((acc) =>
        acc._id === id
          ? {
              ...acc,
              accessToken: undefined,
              refreshToken: undefined,
              wsToken: undefined,
              tokenExpiredAt: undefined,
              signature: undefined,
              loginInfo: undefined,
              customerId: undefined,
              tokenTime: 'No Token',
              tokenWarning: false,
            }
          : acc,
      ),
    );
    showToast.success(`Đăng xuất ${username || 'tài khoản'} thành công`);
  };

  // ── Grouping ───────────────────────────────────────────────────────

  const groupedUsernames = new Set(
    groups.flatMap((g) => g.members.map((m) => (typeof m === 'string' ? m : m.username))),
  );
  const ungroupedAccounts = accounts.filter((acc) => !groupedUsernames.has(acc.username));

  const isGroupRunning = (g: IGroupData) => {
    const members = g.members as IAccountDataResponse[];
    return members.length > 0 && members.every((m) => m.status === 'RUNNING');
  };
  const runningGroups = groups.filter(isGroupRunning);
  const idleGroups = groups.filter((g) => !isGroupRunning(g));

  // ── Skeleton ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-[#252a38] bg-[#161920] p-4">
          <div className="grid grid-cols-5 gap-3">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse flex-col items-center rounded-lg bg-[#252a35] py-3"
              >
                <div className="mx-auto h-6 w-16 rounded bg-[#323844]" />
                <div className="mx-auto mt-2 h-3 w-12 rounded bg-[#323844]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Statistics values ──────────────────────────────────────────────

  const stats = [
    { label: 'Tài khoản', icon: Users, value: totalAccounts, color: 'text-[#e2e8f0]' },
    { label: 'Đang chạy', icon: Activity, value: activeAccounts, color: 'text-[#3b82f6]' },
    { label: 'Thắng', icon: TrendingUp, value: totalWins, color: 'text-[#22c55e]' },
    { label: 'Thua', icon: TrendingDown, value: totalLosses, color: 'text-[#ef4444]' },
    { label: 'Tỷ lệ thắng', icon: BarChart2, value: winRate, color: 'text-[#f59e0b]' },
  ];

  /** Render 1 ô nhóm theo chế độ xem — Tóm tắt (thẻ gọn) hoặc Chi tiết (thẻ đầy đủ). */
  const renderGroupTile = (group: IGroupData) => {
    if (groupView === 'summary') {
      return (
        <GroupSummaryCard
          key={group._id}
          group={group}
          accounts={accounts}
          selectable={selection.selecting}
          selected={selection.selected.has(String(group._id))}
          onToggleSelect={selection.toggle}
        />
      );
    }
    return (
      <GroupCard
        key={group._id}
        group={group}
        accounts={accounts}
        onUpdateAccount={(id, updater) =>
          setAccounts((prev) => prev.map((acc) => (acc._id === id ? updater(acc) : acc)))
        }
        onRefresh={async () => {
          await Promise.all([fetchAccounts(), fetchGroups()]);
        }}
        onUpdateGroup={(groupId, updater) =>
          setGroups((prev) => prev.map((g) => (g._id === groupId ? updater(g) : g)))
        }
        onDeleteAccount={handleDeleteAccount}
        onGetBalance={handleGetBalance}
        onGetUSDTQR={handleGetUSDTQR}
        onGetHistory={(id) => fetchTransactionHistory(id, 1)}
        onGetBetLogs={handleGetBetLogs}
        onWithdraw={handleOpenWithdraw}
        onEditGroup={(grp) => setGroupEditTarget(grp)}
      />
    );
  };

  return (
    <div className="space-y-6 text-[#e2e8f0]">
      {/* ── Page Header ──────────────────────────────────────────── */}
      <div className="mb-6 flex items-center gap-3.5">
        <div className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#2d4a8a] bg-[#1e2a4a]">
          <Gamepad2 size={20} className="text-[#3b82f6]" />
        </div>
        <div>
          <h1 className="m-0 text-[18px] font-bold text-[#e2e8f0]">Sảnh</h1>
          <p className="m-0 mt-0.5 text-[13px] text-[#64748b]">
            Quản lý và theo dõi hoạt động chơi game
          </p>
        </div>
      </div>

      {/* ── Stats Bar ─────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-xl border border-[#252a38] bg-[#161920] p-4 pt-0">
        {/* Auto-refresh progress bar */}
        <motion.div
          key={refreshKey}
          className="h-0.5 origin-left bg-gradient-to-r from-transparent via-[#3b82f6] to-[#3b82f6]"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 30, ease: 'linear' }}
        />

        <div className="flex flex-col gap-3 pt-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="grid flex-1 grid-cols-5 gap-3">
            {stats.map((stat) => (
              <StatCard
                key={stat.label}
                label={stat.label}
                icon={stat.icon}
                value={stat.value}
                color={stat.color}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Thanh công cụ nhóm: Chi tiết⇄Tóm tắt + Chọn nhóm ─────────── */}
      {groups.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Chi tiết ⇄ Tóm tắt — chế độ xem, độc lập với việc chọn. */}
          <div className="inline-flex rounded-lg border border-[#252a38] bg-[#161920] p-0.5">
            {(
              [
                { key: 'detail', label: 'Chi tiết', Icon: List },
                { key: 'summary', label: 'Tóm tắt', Icon: LayoutGrid },
              ] as const
            ).map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setGroupViewMode(key)}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  groupView === key
                    ? 'bg-[#1e2a4a] text-[#3b82f6]'
                    : 'text-[#64748b] hover:text-[#e2e8f0]'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {selection.selecting && (
              <button
                type="button"
                onClick={toggleSelectAll}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#64748b] transition-colors hover:text-[#e2e8f0]"
              >
                {selectedGroups.length === groups.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
              </button>
            )}
            <button
              type="button"
              onClick={selection.toggleSelecting}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                selection.selecting
                  ? 'border-[#3b82f6] bg-[#1e2a4a] text-[#3b82f6]'
                  : 'border-[#252a38] bg-[#161920] text-[#64748b] hover:text-[#e2e8f0]'
              }`}
            >
              <ListChecks className="h-4 w-4" />
              {selection.selecting ? 'Xong' : 'Chọn nhóm'}
            </button>
          </div>
        </div>
      )}

      {/* ── NHÓM ĐANG HOẠT ĐỘNG ────────────────────────────────────── */}
      {runningGroups.length > 0 && (
        <div className="space-y-4">
          <h4 className="flex items-center gap-2 text-sm font-bold text-[#22c55e]">
            <Activity className="h-4 w-4" />
            NHÓM ĐANG HOẠT ĐỘNG
          </h4>
          <div
            className={
              groupView === 'summary'
                ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3'
                : 'grid grid-cols-1 gap-4 lg:grid-cols-2'
            }
          >
            {runningGroups.map((group) => renderGroupTile(group))}
          </div>
        </div>
      )}

      {/* ── NHÓM CỦA TÔI ──────────────────────────────────────────── */}
      {idleGroups.length > 0 && (
        <div className="space-y-4">
          <h4 className="flex items-center gap-2 text-sm font-bold text-[#e2e8f0]">
            <Users className="h-4 w-4 text-[#3b82f6]" />
            NHÓM CỦA TÔI
          </h4>
          <div
            className={
              groupView === 'summary'
                ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3'
                : 'grid grid-cols-1 gap-6 lg:grid-cols-2'
            }
          >
            {idleGroups.map((group) => renderGroupTile(group))}
          </div>
        </div>
      )}

      {/* ── Ungrouped Accounts ────────────────────────────────────── */}
      <div className="space-y-4">
        <h4 className="flex items-center gap-2 text-sm font-bold text-[#e2e8f0]">
          <Users className="h-4 w-4 text-[#3b82f6]" />
          {groups.length > 0 ? 'Tài khoản chưa phân nhóm' : 'Tài khoản Sunwin'}
        </h4>
        {ungroupedAccounts.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {ungroupedAccounts.map((acc) => (
              <AccountCard
                key={acc.username}
                acc={acc}
                onDelete={handleDeleteAccount}
                onGetBalance={handleGetBalance}
                onGetUSDTQR={handleGetUSDTQR}
                onGetHistory={(id) => fetchTransactionHistory(id, 1)}
                onGetBetLogs={handleGetBetLogs}
                onWithdraw={handleOpenWithdraw}
                onLogin={handleLoginAccount}
                onLogout={handleLogoutAccount}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#252a38] bg-transparent py-10 text-center">
            <Users className="mb-2 h-8 w-8 text-[#64748b]" />
            <span className="text-sm font-medium text-[#94a3b8]">
              {groups.length > 0
                ? 'Tất cả tài khoản đều đã được đưa vào nhóm.'
                : 'Chưa có tài khoản. Thêm tài khoản từ mục "Kho tài khoản".'}
            </span>
          </div>
        )}
      </div>

      {/* ── Crypto Deposit Modal — chọn coin/network từ config live rồi lấy QR ─── */}
      {depositModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d0f14]/80 p-4 backdrop-blur-sm">
          <div className="relative flex w-full max-w-md flex-col items-center rounded-2xl border border-[#252a38] bg-[#161920] p-6 text-center shadow-xl">
            <button
              onClick={() => setDepositModal((prev) => ({ ...prev, isOpen: false }))}
              disabled={depositModal.fetchingAddress}
              className="absolute right-4 top-4 rounded-full p-1 text-[#64748b] transition-colors hover:bg-[#252a35] hover:text-[#e2e8f0] disabled:opacity-50"
            >
              <X className="h-5 w-5" strokeWidth={2.5} />
            </button>

            <h3 className="mb-1 text-lg font-bold text-[#e2e8f0]">Nạp tiền điện tử</h3>
            <p className="mb-5 text-xs font-medium text-[#64748b]">
              Tài khoản:{' '}
              <span className="font-semibold text-[#e2e8f0]">{depositModal.username}</span>
            </p>

            <div className="w-full text-left">
              <CryptoPairPicker
                direction="deposit"
                options={depositModal.options}
                currency={depositModal.currency}
                network={depositModal.network}
                loading={depositModal.loadingOptions}
                disabled={depositModal.fetchingAddress}
                onChange={({ currency, network }) =>
                  // Đổi cặp thì xoá QR cũ để không hiển thị địa chỉ của network khác
                  setDepositModal((prev) => ({
                    ...prev,
                    currency,
                    network,
                    address: '',
                    qrCode: '',
                  }))
                }
              />
            </div>

            {!depositModal.address && (
              <button
                onClick={handleFetchDepositAddress}
                disabled={
                  depositModal.fetchingAddress ||
                  depositModal.loadingOptions ||
                  !depositModal.currency ||
                  !depositModal.network
                }
                className="mt-5 w-full rounded-lg bg-[#3b82f6] py-2.5 text-xs font-bold text-white shadow-sm transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {depositModal.fetchingAddress ? 'Đang lấy địa chỉ...' : 'Lấy địa chỉ nạp'}
              </button>
            )}

            {depositModal.address && (
              <>
                <div className="mb-4 mt-5 rounded-xl border border-[#252a35] bg-[#0d0f14] p-4 shadow-inner">
                  {depositModal.qrCode ? (
                    <img
                      src={`data:image/png;base64,${depositModal.qrCode}`}
                      alt={`${depositModal.currency} ${depositModal.network} QR Code`}
                      className="h-48 w-48 object-contain"
                    />
                  ) : (
                    <div className="flex h-48 w-48 items-center justify-center font-medium text-[#64748b]">
                      Lỗi ảnh QR
                    </div>
                  )}
                </div>

                <div className="mb-5 flex w-full items-center justify-between gap-3 rounded-xl border border-[#252a35] bg-[#0d0f14] p-3.5 text-left">
                  <div className="min-w-0 flex-1">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                      Địa chỉ ví
                    </span>
                    <span className="mt-0.5 block select-all break-all font-mono text-xs font-semibold text-[#e2e8f0]">
                      {depositModal.address}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(depositModal.address);
                      showToast.success('Đã sao chép địa chỉ ví thành công!');
                    }}
                    className="shrink-0 rounded-lg bg-[#3b82f6]/10 px-2 py-2 text-xs font-bold text-[#3b82f6] shadow-sm transition-colors hover:bg-[#3b82f6]/20"
                    title="Sao chép"
                  >
                    Sao chép
                  </button>
                </div>

                <div className="flex w-full gap-2 rounded-xl border border-[#f59e0b]/20 bg-[#f59e0b]/10 p-3 text-left">
                  <svg
                    className="mt-0.5 h-5 w-5 shrink-0 text-[#f59e0b]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-[10px] font-semibold leading-normal text-[#f59e0b]">
                    Chỉ gửi <strong>{depositModal.currency}</strong> qua mạng lưới{' '}
                    <strong>{depositModal.network}</strong> vào địa chỉ này. Gửi nhầm có thể làm mất
                    tài sản vĩnh viễn.
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Group Config Popup — tên nhóm, game và cấu hình mọi thành viên ── */}
      {groupEditTarget && (
        <GroupConfigPopup
          isOpen={!!groupEditTarget}
          onClose={() => setGroupEditTarget(null)}
          group={groupEditTarget}
          onSaved={async () => {
            // PUT trả về group thô (members chưa enrich) nên refetch để có lại
            // username/balance/config string cho card.
            await Promise.all([fetchAccounts(), fetchGroups()]);
          }}
        />
      )}

      {/* ── Withdraw Crypto Modal ──────────────────────────────────── */}
      {withdrawModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d0f14]/80 p-4 backdrop-blur-sm">
          <div className="relative flex w-full max-w-md flex-col rounded-2xl border border-[#252a38] bg-[#161920] p-6 shadow-xl">
            <button
              onClick={() => setWithdrawModal((prev) => ({ ...prev, isOpen: false }))}
              disabled={withdrawModal.submitting}
              className="absolute right-4 top-4 rounded-full p-1 text-[#64748b] transition-colors hover:bg-[#252a35] hover:text-[#e2e8f0] disabled:opacity-50"
            >
              <X className="h-5 w-5" strokeWidth={2.5} />
            </button>

            <h3 className="mb-1 text-lg font-bold text-[#e2e8f0]">Rút tiền điện tử</h3>
            <p className="mb-5 text-xs font-medium text-[#64748b]">
              Tài khoản:{' '}
              <span className="font-semibold text-[#e2e8f0]">{withdrawModal.username}</span>
            </p>

            <div className="space-y-4">
              <CryptoPairPicker
                direction="withdraw"
                options={withdrawModal.options}
                currency={withdrawModal.currency}
                network={withdrawModal.network}
                loading={withdrawModal.loadingOptions}
                disabled={withdrawModal.submitting}
                onChange={({ currency, network }) =>
                  setWithdrawModal((prev) => ({ ...prev, currency, network }))
                }
              />

              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-[#64748b]">
                  {withdrawModal.currency && withdrawModal.network
                    ? `Địa chỉ ví ${withdrawModal.currency} (${withdrawModal.network})`
                    : 'Địa chỉ ví nhận'}
                </label>
                <input
                  type="text"
                  value={withdrawModal.address}
                  onChange={(e) =>
                    setWithdrawModal((prev) => ({ ...prev, address: e.target.value }))
                  }
                  placeholder={
                    withdrawModal.network
                      ? `Nhập địa chỉ ví ${withdrawModal.network}...`
                      : 'Chọn mạng lưới trước...'
                  }
                  disabled={!withdrawModal.network}
                  className="w-full rounded-lg border border-[#252a35] bg-[#0d0f14] px-3 py-2.5 font-mono text-xs text-[#e2e8f0] outline-none transition-colors focus:border-[#3b82f6] disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-[#64748b]">
                  Số tiền rút (VND)
                </label>
                <input
                  type="number"
                  value={withdrawModal.amount}
                  onChange={(e) =>
                    setWithdrawModal((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  placeholder="Tối thiểu 200.000 VND"
                  min={200000}
                  className="w-full rounded-lg border border-[#252a35] bg-[#0d0f14] px-3 py-2.5 text-sm text-[#e2e8f0] outline-none transition-colors focus:border-[#3b82f6]"
                />
                <p className="mt-1 text-[10px] font-medium text-[#64748b]">
                  Số tiền rút tối thiểu: 200.000 VND
                </p>
              </div>
            </div>

            <div className="mt-5 flex gap-2 rounded-xl border border-[#f59e0b]/20 bg-[#f59e0b]/10 p-3 text-left">
              <svg
                className="mt-0.5 h-5 w-5 shrink-0 text-[#f59e0b]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-[10px] font-semibold leading-normal text-[#f59e0b]">
                {withdrawModal.network ? (
                  <>
                    Đảm bảo địa chỉ ví là mạng lưới <strong>{withdrawModal.network}</strong>. Rút
                    sai mạng lưới có thể làm mất tài sản vĩnh viễn.
                  </>
                ) : (
                  <>
                    Mạng lưới rút do hệ thống quy định và có thể thay đổi — luôn kiểm tra mạng lưới
                    đang chọn trước khi dán địa chỉ ví.
                  </>
                )}
              </span>
            </div>

            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setWithdrawModal((prev) => ({ ...prev, isOpen: false }))}
                disabled={withdrawModal.submitting}
                className="flex-1 rounded-lg border border-[#252a35] bg-transparent py-2.5 text-xs font-bold text-[#64748b] transition-colors hover:bg-[#252a35] disabled:opacity-50"
              >
                Huỷ
              </button>
              <button
                onClick={handleWithdrawSubmit}
                disabled={
                  withdrawModal.submitting ||
                  withdrawModal.loadingOptions ||
                  !withdrawModal.currency ||
                  !withdrawModal.network
                }
                className="flex-1 rounded-lg bg-[#ef4444] py-2.5 text-xs font-bold text-white shadow-sm transition-colors hover:bg-[#dc2626] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {withdrawModal.submitting ? 'Đang xử lý...' : 'Xác nhận rút'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transaction History Modal ──────────────────────────────── */}
      {historyModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d0f14]/80 p-4 backdrop-blur-sm">
          <div className="relative flex max-h-[85vh] w-full max-w-4xl flex-col rounded-2xl border border-[#252a38] bg-[#161920] p-6 shadow-xl">
            <button
              onClick={() => setHistoryModal((prev) => ({ ...prev, isOpen: false }))}
              className="absolute right-4 top-4 rounded-full p-1 text-[#64748b] transition-colors hover:bg-[#252a35] hover:text-[#e2e8f0]"
            >
              <X className="h-5 w-5" strokeWidth={2.5} />
            </button>

            <h3 className="mb-1 text-lg font-bold text-[#e2e8f0]">Lịch sử Giao dịch Sunwin</h3>
            <p className="mb-4 text-xs font-medium text-[#64748b]">
              Tài khoản:{' '}
              <span className="font-semibold text-[#e2e8f0]">{historyModal.username}</span>
              {historyModal.syncing && (
                <span className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-[#f59e0b]/10 px-2.5 py-0.5 text-[10px] font-bold text-[#f59e0b]">
                  <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Đang đồng bộ...
                </span>
              )}
            </p>

            <div className="mb-4 flex border-b border-[#252a35]">
              {[
                { key: 1, label: 'Lịch sử Nạp (Deposit)' },
                { key: 2, label: 'Lịch sử Rút (Withdrawal)' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleSwitchSlipType(tab.key)}
                  className={`border-b-2 px-4 py-2 text-sm font-bold transition-all ${
                    historyModal.slipType === tab.key
                      ? 'border-[#3b82f6] text-[#3b82f6]'
                      : 'border-transparent text-[#64748b] hover:text-[#e2e8f0]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="scrollbar-thin flex-1 overflow-y-auto">
              {historyModal.loading ? (
                <div className="py-12 text-center text-sm font-semibold text-[#64748b]">
                  Đang tải lịch sử giao dịch...
                </div>
              ) : historyModal.items.length === 0 ? (
                <div className="py-12 text-center text-sm font-medium text-[#64748b]">
                  Không tìm thấy giao dịch nào.
                </div>
              ) : (
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="border-b border-[#252a35] text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                      <th className="pb-2">Mã giao dịch</th>
                      <th className="pb-2">Thời gian GD</th>
                      <th className="pb-2">Số tiền</th>
                      <th className="pb-2">Mạng lưới nhận</th>
                      <th className="pb-2">Trạng thái</th>
                      <th className="pb-2">Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#252a35] font-medium">
                    {historyModal.items.map((item: any) => {
                      const requestDate = item.requestTime
                        ? new Date(item.requestTime).toLocaleString('vi-VN')
                        : 'N/A';
                      const formattedAmount = item.amount
                        ? item.amount.toLocaleString('vi-VN') + ' VND'
                        : '0 VND';
                      return (
                        <tr
                          key={item.id || item.transactionId}
                          className="transition-colors hover:bg-[#252a35]/50"
                        >
                          <td className="max-w-[180px] break-all py-3 font-mono font-bold text-[#e2e8f0]">
                            {item.transactionCode || 'N/A'}
                          </td>
                          <td className="py-3 text-[#64748b]">{requestDate}</td>
                          <td className="py-3 font-bold text-[#e2e8f0]">{formattedAmount}</td>
                          <td className="py-3 font-mono text-[10px] text-[#64748b]">
                            {item.bankReceive?.accountNumber ||
                              item.bankReceive?.accountName ||
                              'N/A'}
                          </td>
                          <td className="py-3">
                            <span
                              className={`rounded px-2 py-0.5 text-[10px] font-extrabold uppercase ${
                                item.status === 2
                                  ? 'border border-[#22c55e]/20 bg-[#22c55e]/10 text-[#22c55e]'
                                  : item.status === 3
                                    ? 'border border-[#ef4444]/20 bg-[#ef4444]/10 text-[#ef4444]'
                                    : 'border border-[#f59e0b]/20 bg-[#f59e0b]/10 text-[#f59e0b]'
                              }`}
                            >
                              {item.statusDescription || 'Đang xử lý'}
                            </span>
                          </td>
                          <td className="py-3 italic text-[#64748b]">{item.notes || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Bet Log Modal ──────────────────────────────────────────── */}
      {betLogModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d0f14]/80 p-4 backdrop-blur-sm">
          <div className="relative flex max-h-[85vh] w-full max-w-4xl flex-col rounded-2xl border border-[#252a38] bg-[#161920] p-6 shadow-xl">
            <button
              onClick={() => setBetLogModal((prev) => ({ ...prev, isOpen: false }))}
              className="absolute right-4 top-4 rounded-full p-1 text-[#64748b] transition-colors hover:bg-[#252a35] hover:text-[#e2e8f0]"
            >
              <X className="h-5 w-5" strokeWidth={2.5} />
            </button>

            <h3 className="mb-1 text-lg font-bold text-[#e2e8f0]">
              Lịch sử Cược — {BETLOG_PAGE_SIZE} ván gần nhất
            </h3>
            <p className="mb-4 text-xs font-medium text-[#64748b]">
              Tài khoản:{' '}
              <span className="font-semibold text-[#e2e8f0]">{betLogModal.username}</span>
              {betLogModal.syncing && (
                <span className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-[#f59e0b]/10 px-2.5 py-0.5 text-[10px] font-bold text-[#f59e0b]">
                  <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Đang đồng bộ...
                </span>
              )}
            </p>

            <div className="scrollbar-thin flex-1 overflow-y-auto">
              {betLogModal.loading ? (
                <div className="py-12 text-center text-sm font-semibold text-[#64748b]">
                  Đang tải lịch sử cược...
                </div>
              ) : betLogModal.items.length === 0 ? (
                <div className="py-12 text-center text-sm font-medium text-[#64748b]">
                  Không tìm thấy lịch sử cược nào.
                </div>
              ) : (
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="border-b border-[#252a35] text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                      <th className="pb-2">Thời gian</th>
                      <th className="pb-2">Dịch vụ</th>
                      <th className="pb-2">Mô tả</th>
                      <th className="pb-2">Thay đổi</th>
                      <th className="pb-2">Số dư sau</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#252a35] font-medium">
                    {betLogModal.items.slice(0, BETLOG_PAGE_SIZE).map((item: any, idx: number) => {
                      const createdDate = item.createdTime
                        ? new Date(item.createdTime * 1000).toLocaleString('vi-VN')
                        : 'N/A';
                      const exchange = item.exchangeValue ?? 0;
                      const formattedExchange = exchange.toLocaleString('vi-VN');
                      return (
                        <tr
                          key={item._id || idx}
                          className="transition-colors hover:bg-[#252a35]/50"
                        >
                          <td className="py-3 text-[#64748b]">{createdDate}</td>
                          <td className="py-3 font-semibold text-[#e2e8f0]">
                            {item.serviceName || 'N/A'}
                          </td>
                          <td className="max-w-[300px] break-words py-3 text-[#e2e8f0]">
                            {item.description || '-'}
                          </td>
                          <td
                            className={`py-3 font-bold ${
                              exchange >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'
                            }`}
                          >
                            {exchange >= 0 ? '+' : ''}
                            {formattedExchange}
                          </td>
                          <td className="py-3 font-semibold text-[#e2e8f0]">
                            {(item.closingValue ?? 0).toLocaleString('vi-VN')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Thao tác hàng loạt (chọn nhiều nhóm) ─────────────────────── */}
      {selection.selecting && (
        <BulkActionBar
          count={selectedGroups.length}
          startCount={eligibleCounts.start}
          stopCount={eligibleCounts.stop}
          busy={bulkBusy}
          onAction={openBulk}
          onClear={selection.clear}
        />
      )}
      <BulkActionSheet
        open={!!bulkPending}
        action={bulkPending?.action ?? 'start'}
        run={bulkPending?.run ?? []}
        skip={bulkPending?.skip ?? []}
        busy={bulkBusy}
        onConfirm={() => void confirmBulk()}
        onClose={() => setBulkPending(null)}
      />
      <BulkResultModal data={bulkResults} onClose={() => setBulkResults(null)} />
    </div>
  );
};
